import { isFamilyGoal, type FamilyCountGoal, type FamilyOfflineCountGoal, type Goal } from "@/lib/schemas";

/**
 * Display helpery dla goali — kolory, etykiety jednostek, copy.
 * Source of truth: GoalKind → wszystkie warianty UI mapują się tutaj.
 */
export type GoalDisplayMeta = {
  emoji: string;
  unitLabel: string;
  unitLabelGen: string;
  shortLabel: string;
  /** Kolor akcentowy — CSS var name (np. "purple", "help", "offline"). */
  accentVar: "purple" | "help" | "offline";
  gradientFrom: string;
  gradientTo: string;
  shadow: string;
};

const META_BY_KIND: Record<Goal["kind"], GoalDisplayMeta> = {
  individual_money: {
    emoji: "💰",
    unitLabel: "zł",
    unitLabelGen: "zł",
    shortLabel: "Mój cel",
    accentVar: "purple",
    gradientFrom: "#8E7DF2",
    gradientTo: "#7565E8",
    shadow: "0 8px 22px -10px rgba(142,125,242,0.5)",
  },
  family_count: {
    emoji: "💛",
    unitLabel: "zadań pomocy",
    unitLabelGen: "zad.",
    shortLabel: "Cel rodziny — pomoc",
    accentVar: "help",
    gradientFrom: "#F59E0B",
    gradientTo: "#D97706",
    shadow: "0 8px 22px -10px rgba(245,158,11,0.55)",
  },
  family_offline_count: {
    emoji: "🌳",
    unitLabel: "zadań poza ekranem",
    unitLabelGen: "zad.",
    shortLabel: "Cel rodziny — poza ekranem",
    accentVar: "offline",
    gradientFrom: "#92744F",
    gradientTo: "#6F5638",
    shadow: "0 8px 22px -10px rgba(146,116,79,0.55)",
  },
};

export function goalDisplay(goal: Goal): GoalDisplayMeta {
  return META_BY_KIND[goal.kind];
}

export function goalDisplayByKind(kind: Goal["kind"]): GoalDisplayMeta {
  return META_BY_KIND[kind];
}

/**
 * Format progresu "X / Y" dla danego kind. Liczbowy — bez procentów.
 * `progress` dla individual_money to balanceMinor, dla family to count.
 */
export function formatGoalProgress(
  goal: Goal,
  progress: number,
): { current: string; target: string; remaining: string } {
  if (goal.kind === "individual_money") {
    const fmt = (m: number) =>
      (m / 100).toFixed(2).replace(".", ",").replace(/,00$/, "");
    return {
      current: `${fmt(progress)} zł`,
      target: `${fmt(goal.targetMinor)} zł`,
      remaining: `${fmt(Math.max(0, goal.targetMinor - progress))} zł`,
    };
  }
  return {
    current: String(progress),
    target: String(goal.targetCount),
    remaining: String(Math.max(0, goal.targetCount - progress)),
  };
}

/** Liczba docelowa — abstrahuje od kind. */
export function goalTargetValue(goal: Goal): number {
  return goal.kind === "individual_money" ? goal.targetMinor : goal.targetCount;
}

/**
 * Ile dni cel rodziny zostaje widoczny na dashboardzie po osiągnięciu —
 * banderoła "Cel osiągnięty!" zamiast znikania od razu (UX: feedback
 * sukcesu dla całej rodziny). Bez push, bez konfetti — zgodnie z
 * filozofią "brak dark patterns" w PRODUCT.md.
 */
export const FAMILY_GOAL_CELEBRATION_DAYS = 5;

export type FamilyGoalDisplayPick = {
  goal: FamilyCountGoal | FamilyOfflineCountGoal;
  isCelebrating: boolean;
};

/**
 * Wybiera cel rodziny do wyświetlenia na dashboardzie:
 *   1) aktywny (bez `completedAt`) — priorytet
 *   2) najświeższy osiągnięty (`completedAt` w oknie `FAMILY_GOAL_CELEBRATION_DAYS`)
 *   3) null
 *
 * Po osiągnięciu i upływie okna celebracji cel znika permanentnie z UI
 * (zostaje w bazie do historii i achievementów).
 */
export function pickFamilyGoalForDisplay(
  householdGoals: Goal[],
): FamilyGoalDisplayPick | null {
  const families = householdGoals.filter(isFamilyGoal);
  const active = families.find((g) => !g.completedAt);
  if (active) return { goal: active, isCelebrating: false };

  const recentlyDone = families
    .filter((g) => g.completedAt)
    .sort(
      (a, b) =>
        (b.completedAt?.toMillis() ?? 0) - (a.completedAt?.toMillis() ?? 0),
    )[0];
  if (!recentlyDone?.completedAt) return null;

  const ageMs = Date.now() - recentlyDone.completedAt.toMillis();
  if (ageMs <= FAMILY_GOAL_CELEBRATION_DAYS * 86400000) {
    return { goal: recentlyDone, isCelebrating: true };
  }
  return null;
}
